import React, { Suspense, lazy, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { DataProvider } from './contexts/DataContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { User, UserRole } from './types';
import BottomNavigation from './components/BottomNavigation';
import { registerPushNotifications } from './utils/notifications';

// Debug logging for App component
const appDebugLog = (message: string, type: 'info' | 'warn' | 'error' = 'info') => {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] [APP] [${type.toUpperCase()}] ${message}`);
};

// Lazy load screens for better performance
const LoginScreen = lazy(() => import('./screens/LoginScreen'));
const RegisterScreen = lazy(() => import('./screens/RegisterScreen'));
const UserDashboard = lazy(() => import('./screens/user/UserDashboard'));
const PostDetails = lazy(() => import('./screens/user/PostDetails'));
const MyRequests = lazy(() => import('./screens/user/MyRequests'));
const MyPackages = lazy(() => import('./screens/user/MyPackages'));
const MyBalance = lazy(() => import('./screens/user/MyBalance'));
const UserProfile = lazy(() => import('./screens/user/UserProfile'));
const RequestApproval = lazy(() => import('./screens/user/RequestApproval'));

const AdminLayout = lazy(() => import('./screens/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./screens/admin/AdminDashboard'));
const ManagePosts = lazy(() => import('./screens/admin/ManagePosts'));
const ManageRequests = lazy(() => import('./screens/admin/ManageRequests'));
const GenerateQR = lazy(() => import('./screens/admin/GenerateQR'));
const ScanQR = lazy(() => import('./screens/admin/ScanQR'));
const ManageUsers = lazy(() => import('./screens/admin/ManageUsers'));
const ManageOffices = lazy(() => import('./screens/admin/ManageOffices'));
const ManageWorkers = lazy(() => import('./screens/admin/ManageWorkers'));
const ManageBalances = lazy(() => import('./screens/admin/ManageBalances'));
const ManagePackages = lazy(() => import('./screens/admin/ManagePackages'));
const PackageDetails = lazy(() => import('./screens/admin/PackageDetails'));
const ManageSellers = lazy(() => import('./screens/admin/ManageSellers'));
const ManageLostPackages = lazy(() => import('./screens/admin/ManageLostPackages'));
const ManageShortageDeliveries = lazy(() => import('./screens/admin/ManageShortageDeliveries'));
const ManageWithdrawalRequests = lazy(() => import('./screens/admin/ManageWithdrawalRequests'));
const AllTransactions = lazy(() => import('./screens/admin/AllTransactions'));
const OfficeTransactions = lazy(() => import('./screens/admin/OfficeTransactions'));
const ActivityLog = lazy(() => import('./screens/admin/ActivityLog'));
const ManageUserApprovals = lazy(() => import('./screens/admin/ManageUserApprovals'));
const ManageNotifications = lazy(() => import('./screens/admin/ManageNotifications'));
const PackageInfo = lazy(() => import('./screens/public/PackageInfo'));

// Loading component
const LoadingFallback: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-text-secondary">Loading...</p>
      </div>
    </div>
  );
};

// Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-text-primary mb-4">Something went wrong</h1>
            <p className="text-text-secondary mb-4">
              The application encountered an error. Please try restarting the app.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg"
            >
              Reload App
            </button>
            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-text-secondary">Error Details</summary>
                <pre className="mt-2 text-xs text-red-500 overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  appDebugLog('App component rendering...', 'info');
  
  useEffect(() => {
    appDebugLog('App component mounted successfully', 'info');
    
    // Global timeout to force loading states to false after 10 seconds
    // This prevents the app from hanging indefinitely if contexts are stuck
    const globalTimeout = setTimeout(() => {
      appDebugLog('Global timeout reached - forcing loading states to false', 'warn');
      // Try to force loading states via context if possible
      // This is a safety net in case contexts hang
      const event = new CustomEvent('force-loading-false');
      window.dispatchEvent(event);
    }, 10000);
    
    return () => {
      clearTimeout(globalTimeout);
      appDebugLog('App component unmounting', 'info');
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <DataProvider>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <MainApp />
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </DataProvider>
    </ErrorBoundary>
  );
};

const MainApp: React.FC = () => {
  const { lang, dir } = useLanguage();
  const { loading: authLoading } = useAuth();

  useEffect(() => {
    appDebugLog('MainApp component rendering...', 'info');
    appDebugLog('Auth loading state: ' + authLoading, 'info');
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    appDebugLog('MainApp component mounted, language: ' + lang + ', direction: ' + dir, 'info');
  }, [lang, dir, authLoading]);

  return (
    <HashRouter>
      <AppRoutes />
    </HashRouter>
  );
};

const AppRoutes: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  
  // Log auth state for debugging
  useEffect(() => {
    appDebugLog('AppRoutes: authLoading=' + authLoading + ', user=' + (user ? user.id : 'null'), 'info');
  }, [authLoading, user]);
  
  // Register push notifications when user is logged in
  useEffect(() => {
    if (user?.id) {
      try {
        registerPushNotifications(user.id).catch(err => {
          console.error('Failed to register push notifications:', err);
        });
      } catch (error) {
        console.error('Error registering push notifications:', error);
      }
    }
  }, [user?.id]);
  
  // Public routes (accessible without authentication)
  const publicRoutes = (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/package-info/:data" element={<PackageInfo />} />
        <Route path="/package-info" element={<PackageInfo />} />
        {!user && (
          <>
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        )}
      </Routes>
    </Suspense>
  );

  if (!user) {
    return publicRoutes;
  }

  const renderUserRoutes = () => {
    // Check if user is approved (for Normal users)
    const isApproved = user.role !== UserRole.Normal || user.approved === true;
    
    // If not approved, show approval request screen
    if (!isApproved) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/package-info/:data" element={<PackageInfo />} />
            <Route path="/package-info" element={<PackageInfo />} />
            <Route path="/request-approval" element={<RequestApproval />} />
            <Route path="*" element={<Navigate to="/request-approval" />} />
          </Routes>
        </Suspense>
      );
    }

    // Approved users can access normal routes
    return (
      <>
        <div
          className="lg:!pb-0"
          style={{ paddingBottom: 'calc(var(--bottom-nav-height, 5.5rem) + env(safe-area-inset-bottom, 0px))' }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/package-info/:data" element={<PackageInfo />} />
              <Route path="/package-info" element={<PackageInfo />} />
              <Route path="/" element={<UserDashboard />} />
              <Route path="/post/:id" element={<PostDetails />} />
              <Route path="/requests" element={<MyRequests />} />
              <Route path="/packages" element={<MyPackages />} />
              <Route path="/balance" element={<MyBalance />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </div>
        <BottomNavigation />
      </>
    );
  };

  const renderAdminRoutes = () => (
    <Suspense fallback={<LoadingFallback />}>
      <AdminLayout>
        <Routes>
          <Route path="/package-info/:data" element={<PackageInfo />} />
          <Route path="/package-info" element={<PackageInfo />} />
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/posts" element={<ManagePosts />} />
          <Route path="/post/:id" element={<PostDetails />} />
          <Route path="/sellers" element={<ManageSellers />} />
          <Route path="/requests" element={<ManageRequests />} />
          <Route path="/packages" element={<ManagePackages />} />
          <Route path="/package/:id" element={<PackageDetails />} />
          <Route path="/lost-packages" element={<ManageLostPackages />} />
          <Route path="/shortage-deliveries" element={<ManageShortageDeliveries />} />
          <Route path="/withdrawal-requests" element={<ManageWithdrawalRequests />} />
          <Route path="/generate-qr" element={<GenerateQR />} />
          <Route path="/scan-qr" element={<ScanQR />} />
          {(user.role === UserRole.SuperAdmin || user.role === UserRole.Office) && (
            <Route path="/user-approvals" element={<ManageUserApprovals />} />
          )}
          {user.role === UserRole.SuperAdmin && (
            <>
              <Route path="/users" element={<ManageUsers />} />
              <Route path="/offices" element={<ManageOffices />} />
              <Route path="/balances" element={<ManageBalances />} />
              <Route path="/all-transactions" element={<AllTransactions />} />
              <Route path="/activity-log" element={<ActivityLog />} />
              <Route path="/notifications" element={<ManageNotifications />} />
            </>
          )}
          {user.role === UserRole.Office && (
            <>
              <Route path="/workers" element={<ManageWorkers />} />
              <Route path="/office-transactions" element={<OfficeTransactions />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AdminLayout>
    </Suspense>
  );

  switch (user.role) {
    case UserRole.Normal:
      return renderUserRoutes();
    case UserRole.AdminA:
    case UserRole.AdminB:
    case UserRole.SuperAdmin:
    case UserRole.Office:
    case UserRole.Worker:
      return renderAdminRoutes();
    default:
      return <Navigate to="/login" />;
  }
};

export default App;
